import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MemberAvatar } from "@/components/MemberAvatar";
import { RoleBadge } from "@/components/RoleBadge";
import type { Member, RoleSource } from "@/lib/api";

interface MemberRowProps {
  member: Member;
  onGrant: (m: Member) => void;
  onRevoke: (m: Member) => void;
}

const SOURCE_LABELS: Record<RoleSource, string | null> = {
  super:       "Super admin",
  admin:       "Suite admin",
  direct:      null, // computed dynamically from grantedBy — see getDirectLabel
  "user-grant":"Org grant (user)",
  group:       "Group grant",
  org:         "Org grant",
  tier:        "Tier default",
  "cross-org": "Cross-org",
  none:        null,
};

function getDirectLabel(grantedBy: string | null | undefined, grantedByName: string | null | undefined): { label: string; tooltip: string } {
  if (!grantedBy || grantedBy === "__service__") {
    return { label: "Tool override", tooltip: "Set via the tool's planner" };
  }
  const name = grantedByName ?? grantedBy;
  return { label: "Admin override", tooltip: `Set by ${name}` };
}

const SOURCE_COLORS: Record<RoleSource, string> = {
  super:       "bg-purple-100 text-purple-700 border-purple-200",
  admin:       "bg-indigo-100 text-indigo-700 border-indigo-200",
  direct:      "bg-amber-100 text-amber-700 border-amber-200",
  "user-grant":"bg-sky-100 text-sky-700 border-sky-200",
  group:       "bg-sky-100 text-sky-700 border-sky-200",
  org:         "bg-slate-100 text-slate-600 border-slate-200",
  tier:        "bg-slate-100 text-slate-600 border-slate-200",
  "cross-org": "bg-teal-100 text-teal-700 border-teal-200",
  none:        "",
};

export function MemberRow({ member, onGrant, onRevoke }: MemberRowProps) {
  const isOrgAdmin = member.orgRole === "org:admin";
  const hasExplicit = member.toolRole !== "none";
  const source = member.toolRoleSource;
  const isDirect = source === "direct";
  const directInfo = isDirect ? getDirectLabel(member.toolRoleGrantedBy, member.toolRoleGrantedByName) : null;
  const sourceLabel = isDirect ? directInfo!.label : (source ? SOURCE_LABELS[source] : null);
  const sourceColor = source ? SOURCE_COLORS[source] : "";
  const sourceTooltip = isDirect ? directInfo!.tooltip : undefined;
  return (
    <li className="border-b border-slate-100 py-3 last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <MemberAvatar name={member.displayName} imageUrl={member.imageUrl} />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-slate-900">
              {member.displayName ?? member.email ?? member.clerkUserId}
            </div>
            {member.email && (
              <div className="truncate text-xs text-slate-500">{member.email}</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isOrgAdmin && (
            <span className="inline-flex items-center rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
              org-admin
            </span>
          )}
          {sourceLabel && (
            <span
              title={sourceTooltip}
              className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${sourceColor}${sourceTooltip ? " cursor-help" : ""}`}
            >
              {sourceLabel}
            </span>
          )}
          <RoleBadge role={member.toolRole} />
          {hasExplicit && (
            <>
              <Button size="sm" variant="outline" onClick={() => onGrant(member)}>
                Change
              </Button>
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Revoke access for ${member.displayName ?? member.email ?? "member"}`}
                onClick={() => onRevoke(member)}
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
          {!hasExplicit && (
            <Button size="sm" onClick={() => onGrant(member)}>
              + Grant
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

export function MemberRowSkeleton() {
  return (
    <li className="flex items-center justify-between gap-3 border-b border-slate-100 py-3 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 animate-pulse rounded-full bg-slate-200" />
        <div className="space-y-1.5">
          <div className="h-3.5 w-40 animate-pulse rounded bg-slate-200" />
          <div className="h-3 w-56 animate-pulse rounded bg-slate-100" />
        </div>
      </div>
      <div className="h-5 w-20 animate-pulse rounded bg-slate-200" />
    </li>
  );
}
